import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Container } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  selectGetCompanyEnrollmentInformationStatus,
  selectMemberInformation,
  selectMemberSession,
  selectOptimizelyClientStatus,
} from '../store/slices/onboardingSlice';
import { logout } from '../store/thunks/authenticationThunk';
import { isOnboarding } from '../routes/namedRoutes';
import { ReactCustomerIO } from '../config/customerIo';
import { ReactChameleon } from '../config/chameleon';
import { ReactHeap } from '../config/heap';
import { useSolutionOrg } from '../config/useSolutionOrg';
import { ServiceNationSolutionOrgName } from '../common/constants';
import { OnboardingFlowTestFlag } from '../common/OptimizelyFlagsConstants';
import { AppOptimizelyProvider } from '../config/AppOptimizelyProvider';
import { normalizeOptimizelyEmail } from '../common/utils/optimizelyAttributes';
import { resolveHeapPlan } from '../common/models/Subscription';

interface AuthenticatedRouteProps {
  children: JSX.Element
}

/* const tokenExpired = (tokenExpiry: string | null): boolean => {
  if (!tokenExpiry) {
    // If tokenExpiry is not provided, consider it as expired
    return true;
  }

  try {
    const tokenExpiryDate = new Date(tokenExpiry);
    const currentDate = new Date();

    // Use the getTime() method to compare timestamps, which is more reliable
    // check if expired
    if (currentDate.getTime() > tokenExpiryDate.getTime()) {
      return true;
    }
  } catch (error) {
    // Handle the parsing error, log it, and consider the token expired
    console.error('Error parsing tokenExpiry:', error);
    return true;
  }

  return false;
}; */

export default function AuthenticatedRoute({ children }: AuthenticatedRouteProps) {
  const { accessToken, memberId, memberEmail } = useAppSelector(selectMemberSession);
  const {
    id, firstName, lastName, edgeEnrollmentDate, company, solutionOrgMemberKey,
  } = useAppSelector(selectMemberInformation);
  const getCompanyEnrollmentInformationStatus =
    useAppSelector(selectGetCompanyEnrollmentInformationStatus);
  const optimizelyClientStatus = useAppSelector(selectOptimizelyClientStatus);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const { solutionOrgName } = useSolutionOrg();

  // Byte-exact in Optimizely's audiences, so it has to be normalised before any
  // decision - see normalizeOptimizelyEmail.
  const optimizelyEmail = normalizeOptimizelyEmail(memberEmail);

  useEffect(() => {
    if (!accessToken) {
      dispatch(logout({ isOnboarding: isOnboarding(), navigate }));
    } else {
      setIsLoading(false);
      // TODO is there a better place for this?
      if (global.heap) { global.heap?.identify(memberId); }

      // Only use memberInformation when it matches the current session
      // to avoid using stale redux-persist data from previous user sessions
      const memberInfoMatchesSession = id === memberId;

      const heapPlan = resolveHeapPlan({
        company,
        isServiceNation: solutionOrgName === ServiceNationSolutionOrgName,
      });
      if (memberInfoMatchesSession && heapPlan) {
        ReactHeap.setPlan(heapPlan);
      }

      // Skip Customer.io identification for Service Nation users (they use HubSpot)
      // Only identify edge-enrolled members
      const shouldIdentify = memberInfoMatchesSession &&
        solutionOrgName !== ServiceNationSolutionOrgName &&
        !!edgeEnrollmentDate;
      if (shouldIdentify) {
        ReactCustomerIO.identify(memberId);
      }

      let onboardingFlowTestGroup = '';
      if (global.optimizely && optimizelyClientStatus === 'success') {
        const onboardingDecision = global.optimizely.decide(
          OnboardingFlowTestFlag,
          undefined,
          memberId,
          { email: optimizelyEmail, solutionOrgName: solutionOrgName as string },
        );
        onboardingFlowTestGroup = onboardingDecision?.variationKey || '';
      }

      // For SN native users
      // Only identify users by using SN user ID to avoid creating duplicate users in Chameleon
      if (solutionOrgName === ServiceNationSolutionOrgName && solutionOrgMemberKey) {
        ReactChameleon.identify(solutionOrgMemberKey);
      } else {
        ReactChameleon.identify(memberId, {
          email: memberEmail,
          firstName: firstName || '',
          lastName: lastName || '',
          edgeEnrolled: !!edgeEnrollmentDate,
          edgeEnrollmentDate: edgeEnrollmentDate || '',
          solutionOrgName: solutionOrgName as string,
          environment: process.env.REACT_APP_ENVIRONMENT || '',
          onboardingFlowTestGroup,
          company: company?.id ? { uid: company.id } : undefined,
          source: 'Edge',
        });
      }
    }
  }, [
    navigate,
    dispatch,
    accessToken,
    memberId,
    memberEmail,
    optimizelyEmail,
    firstName,
    lastName,
    edgeEnrollmentDate,
    solutionOrgName,
    company,
    optimizelyClientStatus,
    id,
    solutionOrgMemberKey,
  ]);

  const component = (
    <AppOptimizelyProvider
      user={{
        id: memberId,
        attributes: {
          email: optimizelyEmail,
          solutionOrgName: solutionOrgName as string,
        },
      }}
    >
      {children}
    </AppOptimizelyProvider>
  );

  return (
    isLoading || getCompanyEnrollmentInformationStatus === 'pending' ? (
      <Container sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}>
        <CircularProgress color="inherit" size={40} />
      </Container>
    ) : (component)
  );
}
